import type { Form, Label } from '@piccolohealth/pbs-common';
import { ChakraV3 } from '@piccolohealth/ui';
import { P } from '@piccolohealth/util';
import React from 'react';
import { FaCopy } from 'react-icons/fa';

import { FormStack } from '../../../components/forms/FormStack';
import { showModal } from '../../../components/generic/Modal';
import { usePermission } from '../../../hooks/usePermission';
import { FormDeleteModal } from './FormDeleteModal';
import { FormResponsesModal } from './FormResponsesModal';
import { FormTypeTag } from './FormTypeTag';
import { FormUpdateModal } from './FormUpdateModal';

interface FormLabelsProps {
  labels: Label[];
}

export const FormLabels = (props: FormLabelsProps) => {
  const { labels } = props;

  if (P.isEmpty(labels)) {
    return (
      <ChakraV3.Text fontSize='xs' color='secondary'>
        No labels
      </ChakraV3.Text>
    );
  }

  return (
    <ChakraV3.Wrap pt={2}>
      {labels.map((label) => {
        return (
          <ChakraV3.WrapItem key={label.id}>
            <ChakraV3.Tag size='sm' colorPalette={label.color}>
              {label.name}
            </ChakraV3.Tag>
          </ChakraV3.WrapItem>
        );
      })}
    </ChakraV3.Wrap>
  );
};

interface FormCardProps {
  form: Form;
}

export const FormCard = (props: FormCardProps) => {
  const { form } = props;

  const updateFormPermission = usePermission('update', 'form');
  const deleteFormPermission = usePermission('delete', 'form');

  return (
    <ChakraV3.Card.Root w='full' h='full'>
      <ChakraV3.Card.Header py={4} px={4}>
        <ChakraV3.HStack>
          <ChakraV3.Heading size='sm' fontWeight='bold' lineClamp={1}>
            {form.name}
          </ChakraV3.Heading>
          <ChakraV3.Spacer />
          <FormTypeTag type={form.type} />
        </ChakraV3.HStack>
      </ChakraV3.Card.Header>
      <ChakraV3.Separator />
      <ChakraV3.Card.Body py={4} px={4}>
        <FormStack gap={6}>
          <ChakraV3.FormField label='Share form'>
            <ChakraV3.InputGroup
              endElement={
                <ChakraV3.Button
                  onClick={() => navigator.clipboard.writeText(form.url ?? '')}
                  h='1.65rem'
                  variant='solid'
                  colorPalette='brand'
                  size='xs'
                  mr={1}
                >
                  <FaCopy />
                  Copy
                </ChakraV3.Button>
              }
            >
              <ChakraV3.Input pr='4.5rem' size='sm' disabled value={form.url ?? ''} />
            </ChakraV3.InputGroup>
          </ChakraV3.FormField>
          <ChakraV3.FormField label='External ID'>
            <ChakraV3.Text fontSize='xs' color='secondary'>
              {form.externalId ?? 'No external ID'}
            </ChakraV3.Text>
          </ChakraV3.FormField>
          <ChakraV3.FormField label='Creation labels'>
            <FormLabels labels={form.labelMappings.create} />
          </ChakraV3.FormField>
          <ChakraV3.FormField label='Update labels'>
            <FormLabels labels={form.labelMappings.update} />
          </ChakraV3.FormField>
        </FormStack>
      </ChakraV3.Card.Body>
      <ChakraV3.Card.Footer px={4} py={2} flexShrink={0}>
        <ChakraV3.HStack>
          <ChakraV3.Button
            onClick={(e) => {
              e.preventDefault();
              showModal(FormDeleteModal, {
                formId: form.id,
                formName: form.name,
              });
            }}
            variant='plain'
            color='danger'
            size='xs'
            disabled={!deleteFormPermission}
          >
            Delete
          </ChakraV3.Button>
          <ChakraV3.Button
            onClick={(e) => {
              e.preventDefault();
              showModal(FormUpdateModal, {
                form,
              });
            }}
            variant='plain'
            size='xs'
            disabled={!updateFormPermission}
          >
            Settings
          </ChakraV3.Button>
          <ChakraV3.Button
            onClick={(e) => {
              e.preventDefault();
              showModal(FormResponsesModal, {
                formId: form.id,
                formName: form.name,
              });
            }}
            variant='plain'
            size='xs'
          >
            View responses
          </ChakraV3.Button>
        </ChakraV3.HStack>
      </ChakraV3.Card.Footer>
    </ChakraV3.Card.Root>
  );
};
